import React, { useState, useEffect, useRef } from 'react';
import { Link } from 'react-router';
import { Grid, Typography, Alert, Button } from '@mui/material';
import { Info as InfoIcon, Edit as EditIcon } from '@mui/icons-material';
import { useDispatch, useSelector } from 'react-redux';
import { useUser } from '@/providers';

import ModelTypeField from './ModelTypeField';
import ModelUriField from './ModelUriField';
import CheckpointDrawer from '@/components/shared/CheckpointDrawer';
import { fetchCheckpoints } from '@/actions/checkpoints';
import { fetchPublicCheckpoints } from '@/actions/publicCheckpoints';
import cleanEmptyValues from '@/util/cleanEmptyValues';

const checkpointErrorCheck = (checkpoints) => {
  const names = checkpoints.map((checkpoint) => {
    if (checkpoint.as) {
      return checkpoint.as.toLowerCase().replace(' ', '_');
    }
    return undefined;
  });
  const dup_names = names.filter((e, i, a) => a.indexOf(e) !== i);
  const errors = checkpoints.map((checkpoint) => {
    if (checkpoint.as) {
      return dup_names.includes(checkpoint.as.toLowerCase().replace(' ', '_'));
    }
    return true;
  });
  return errors;
};

export default function ModelFields({
  initialValues,
  type,
  onChange,
  edit = false,
  copy = false,
}) {
  const dispatch = useDispatch();
  const user = useUser();
  const checkpoints = useSelector((state) => state.checkpoints);
  const publicCheckpoints = useSelector((state) => state.publicCheckpoints);

  // Transform initial values from API format (checkpoint_uuid) to id format
  const transformInitialValues = (values) => {
    if (!values) {
      return {
        source_type: '',
        source_uri: '',
        checkpoints: [],
      };
    }
    return {
      source_type: values.source_type || '',
      source_uri: values.source_uri || '',
      checkpoints: (values.checkpoints || []).map((checkpoint) => ({
        id: checkpoint.checkpoint_uuid || checkpoint.id,
        as: checkpoint.name || checkpoint.as || '',
        public: checkpoint.public || false,
      })),
    };
  };

  const [values, setValues] = useState(transformInitialValues(initialValues));
  const [errors, setErrors] = useState({
    source_uri_error: false,
    checkpoint_errors:
      initialValues && initialValues.checkpoints
        ? new Array(initialValues.checkpoints.length).fill(false)
        : [],
  });

  const [drawerOpen, setDrawerOpen] = useState(false);
  const isInitialMount = useRef(true);

  // Fetch checkpoints on mount
  useEffect(() => {
    if (publicCheckpoints === null) {
      dispatch(fetchPublicCheckpoints());
    }
    if (checkpoints === null) {
      if (user?.selected_project) {
        dispatch(fetchCheckpoints(user.selected_project));
      }
    }
  }, [dispatch, checkpoints, publicCheckpoints, user?.selected_project]);

  // Initial mount - call onChange
  useEffect(() => {
    if (isInitialMount.current) {
      isInitialMount.current = false;
      const checkpoints_filtered = values.checkpoints
        .map((checkpoint) => (!checkpoint.id ? undefined : checkpoint))
        .filter((i) => i);
      const checkpoint_error = new Set(errors.checkpoint_errors);
      const modelValues = {
        source_type: values.source_type,
        source_uri: values.source_uri,
        checkpoints: checkpoints_filtered,
      };
      const cleanedModel = cleanEmptyValues(modelValues);
      onChange(
        'model',
        cleanedModel || {},
        errors.source_uri_error || checkpoint_error.has(true),
      );
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Update onChange when values or errors change
  useEffect(() => {
    if (isInitialMount.current) {
      return;
    }
    const checkpoints_filtered = values.checkpoints
      .map((checkpoint) => (!checkpoint.id ? undefined : checkpoint))
      .filter((i) => i);

    const checkpoint_error = new Set(errors.checkpoint_errors);
    const modelValues = {
      ...values,
      checkpoints: checkpoints_filtered,
    };
    const cleanedModel = cleanEmptyValues(modelValues);
    onChange(
      'model',
      cleanedModel || {},
      errors.source_uri_error || checkpoint_error.has(true),
    );
  }, [values, errors, onChange]);

  const setChanged = () => {
    // This is a no-op but kept for compatibility with field components
  };

  const handleSourceTypeChange = (newValue) => {
    setValues({
      ...values,
      source_type: newValue,
      source_uri: '', // Clear URI when type changes
    });
    setErrors({
      ...errors,
      source_uri_error: false, // Clear error when type changes
    });
  };

  const handleSourceUriChange = (newValue) => {
    setValues({
      ...values,
      source_uri: newValue,
    });
  };

  const handleSourceUriError = (error) => {
    setErrors({
      ...errors,
      source_uri_error: error,
    });
  };

  const handleCheckpointDrawerClose = (newCheckpoints) => {
    const checkpoint_errors = checkpointErrorCheck(newCheckpoints);
    setValues({
      ...values,
      checkpoints: newCheckpoints,
    });
    setErrors({
      ...errors,
      checkpoint_errors,
    });
  };

  const projectCredentials = useSelector((state) => state.projectCredentials);

  return (
    <Grid container spacing={2}>
      <Grid size={12}>
        <ModelTypeField
          value={values.source_type}
          setValue={handleSourceTypeChange}
          setChanged={setChanged}
          edit={edit}
          copy={copy}
        />
      </Grid>
      {values.source_type && (
        <Grid size={12}>
          <ModelUriField
            value={values.source_uri}
            sourceType={values.source_type}
            error={errors.source_uri_error}
            setValue={handleSourceUriChange}
            setError={handleSourceUriError}
            setChanged={setChanged}
            edit={edit}
            copy={copy}
          />
        </Grid>
      )}
      <Grid size={12}>
        <Typography variant='h6'>Checkpoints</Typography>
      </Grid>
      <Grid size={12}>
        <Button
          variant='outlined'
          startIcon={<EditIcon />}
          onClick={() => setDrawerOpen(true)}
        >
          {values.checkpoints && values.checkpoints.length > 0
            ? `Manage Checkpoints (${values.checkpoints.length})`
            : 'Add Checkpoints'}
        </Button>
      </Grid>
      <CheckpointDrawer
        open={drawerOpen}
        handleClose={() => setDrawerOpen(false)}
        initialValues={values.checkpoints || []}
        setValue={handleCheckpointDrawerClose}
      />
      {projectCredentials &&
      projectCredentials.filter((key) => key.type === 'git').length === 0 ? (
        <Grid size={12}>
          <Alert
            severity='info'
            icon={<InfoIcon />}
            action={
              <Link
                to='/account/settings'
                target='_blank'
                style={{ textDecoration: 'none' }}
              >
                <Typography variant='body2' color='primary'>
                  Generate a Git SSH Key Here
                </Typography>
              </Link>
            }
          >
            Trying to access a private repo?
          </Alert>
        </Grid>
      ) : undefined}
    </Grid>
  );
}
